// =============================================
// CARGA MASIVA DE DATOS - CON VALIDACIÓN PREVIA
// Valida Excel antes de enviar al servidor
// =============================================

import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import bulkUploadService from '../../services/bulkUploadService';

const BulkUpload = () => {
  // BULK: Estados
  const [activeTab, setActiveTab] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  
  // VALIDACIÓN: Estados
  const [validationErrors, setValidationErrors] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // BULK: Cambiar tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFile(null);
    setResults(null);
    setError('');
    setValidationErrors([]);
    setParsedData([]);
    setShowPreview(false);
  };

  // VALIDACIÓN: Campos requeridos según tipo
  const getRequiredFields = () => {
    if (activeTab === 0) {
      return {
        required: ['amount', 'payment_date', 'month', 'year'],
        optional: ['user_id', 'house_number', 'has_receipt'],
        oneOf: ['user_id', 'house_number'] // Al menos uno de estos
      };
    } else {
      return {
        required: ['category', 'description', 'amount', 'expense_date'],
        optional: ['notes', 'has_receipt']
      };
    }
  };

  // VALIDACIÓN: Validar formato de fecha
  const isValidDate = (dateString) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  // VALIDACIÓN: Validar número
  const isValidNumber = (value) => {
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  // VALIDACIÓN: Validar fila
  const validateRow = (row, rowNumber) => {
    const errors = [];
    const fields = getRequiredFields();

    // Validar campos requeridos
    fields.required.forEach(field => {
      if (!row[field] || row[field] === '') {
        errors.push(`Fila ${rowNumber}: Campo "${field}" es requerido`);
      }
    });

    // Validar "al menos uno de"
    if (fields.oneOf) {
      const hasOneOf = fields.oneOf.some(field => row[field] && row[field] !== '');
      if (!hasOneOf) {
        errors.push(`Fila ${rowNumber}: Debe tener al menos uno de: ${fields.oneOf.join(', ')}`);
      }
    }

    // Validaciones específicas por tipo
    if (activeTab === 0) {
      // PAGOS
      if (row.amount && !isValidNumber(row.amount)) {
        errors.push(`Fila ${rowNumber}: "amount" debe ser un número válido`);
      }
      if (row.amount && parseFloat(row.amount) <= 0) {
        errors.push(`Fila ${rowNumber}: "amount" debe ser mayor a 0`);
      }
      if (row.payment_date && !isValidDate(row.payment_date)) {
        errors.push(`Fila ${rowNumber}: "payment_date" debe tener formato YYYY-MM-DD`);
      }
      if (row.month && (parseInt(row.month) < 1 || parseInt(row.month) > 12)) {
        errors.push(`Fila ${rowNumber}: "month" debe estar entre 1 y 12`);
      }
      if (row.year && (parseInt(row.year) < 2000 || parseInt(row.year) > 2100)) {
        errors.push(`Fila ${rowNumber}: "year" debe ser válido (2000-2100)`);
      }
    } else {
      // GASTOS
      if (row.amount && !isValidNumber(row.amount)) {
        errors.push(`Fila ${rowNumber}: "amount" debe ser un número válido`);
      }
      if (row.amount && parseFloat(row.amount) <= 0) {
        errors.push(`Fila ${rowNumber}: "amount" debe ser mayor a 0`);
      }
      if (row.expense_date && !isValidDate(row.expense_date)) {
        errors.push(`Fila ${rowNumber}: "expense_date" debe tener formato YYYY-MM-DD`);
      }
      if (!row.category || row.category.trim() === '') {
        errors.push(`Fila ${rowNumber}: "category" no puede estar vacío`);
      }
    }

    return errors;
  };

  // VALIDACIÓN: Leer y validar Excel
  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validar tipo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(selectedFile.type)) {
      setError('Solo se permiten archivos Excel (.xlsx, .xls)');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError('');
    setResults(null);
    setValidating(true);
    setValidationErrors([]);
    setParsedData([]);

    try {
      // Leer archivo
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setError('El archivo Excel está vacío');
        setFile(null);
        setValidating(false);
        return;
      }

      // Validar cada fila
      const allErrors = [];
      jsonData.forEach((row, index) => {
        const rowErrors = validateRow(row, index + 2); // +2 por header y porque Excel empieza en 1
        allErrors.push(...rowErrors);
      });

      setValidationErrors(allErrors);
      setParsedData(jsonData);

      if (allErrors.length === 0) {
        setShowPreview(true);
      }

    } catch (err) {
      setError('Error al leer el archivo: ' + err.message);
      setFile(null);
    } finally {
      setValidating(false);
    }
  };

  // BULK: Subir archivo (después de validación)
  const handleUpload = async () => {
    if (!file) {
      setError('Selecciona un archivo primero');
      return;
    }

    if (validationErrors.length > 0) {
      setError('Corrige los errores antes de procesar');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      let response;
      if (activeTab === 0) {
        response = await bulkUploadService.uploadPayments(file);
      } else {
        response = await bulkUploadService.uploadExpenses(file);
      }

      setResults(response.results);
      setFile(null);
      setParsedData([]);
      setShowPreview(false);

    } catch (err) {
      setError(err.response?.data?.message || 'Error al procesar el archivo');
    } finally {
      setLoading(false);
    }
  };

  // BULK: Descargar plantilla
  const handleDownloadTemplate = async () => {
    try {
      if (activeTab === 0) {
        await bulkUploadService.downloadPaymentsTemplate();
      } else {
        await bulkUploadService.downloadExpensesTemplate();
      }
    } catch (err) {
      setError('Error al descargar plantilla');
    }
  };

  // PREVIEW: Cerrar preview
  const handleClosePreview = () => {
    setShowPreview(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* BULK: Título */}
      <Typography variant="h4" sx={{ mb: 3 }}>
        Carga Masiva de Datos
      </Typography>

      {/* BULK: Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Pagos" />
          <Tab label="Gastos" />
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {/* BULK: Instrucciones */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 Instrucciones
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="1. Descargar plantilla"
                    secondary="Usa el botón de abajo para obtener el formato correcto"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="2. Llenar datos"
                    secondary="Completa la plantilla con la información"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="3. Subir archivo"
                    secondary="El sistema validará automáticamente los datos"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="4. Revisar y confirmar"
                    secondary="Verifica los datos y confirma la carga"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              {/* Campos requeridos según tab */}
              <Typography variant="subtitle2" gutterBottom>
                {activeTab === 0 ? '📊 Campos de Pagos:' : '💰 Campos de Gastos:'}
              </Typography>
              
              {activeTab === 0 ? (
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="• user_id o house_number (requerido)"
                      secondary="ID del propietario o número de casa"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• amount (requerido)"
                      secondary="Monto del pago (número mayor a 0)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• payment_date (requerido)"
                      secondary="Fecha formato YYYY-MM-DD (ej: 2026-01-15)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• month y year (requerido)"
                      secondary="Mes (1-12) y año del pago"
                    />
                  </ListItem>
                </List>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="• category (requerido)"
                      secondary="Nombre exacto de la categoría existente"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• description (requerido)"
                      secondary="Descripción del gasto (no vacío)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• amount (requerido)"
                      secondary="Monto del gasto (número mayor a 0)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="• expense_date (requerido)"
                      secondary="Fecha formato YYYY-MM-DD (ej: 2026-01-15)"
                    />
                  </ListItem>
                </List>
              )}

              {/* Botón descargar plantilla */}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                fullWidth
                sx={{ mt: 2 }}
              >
                Descargar Plantilla
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* BULK: Área de upload */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📤 Subir Archivo
              </Typography>

              {/* Selector de archivo */}
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: file ? (validationErrors.length === 0 ? 'success.main' : 'error.main') : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: file ? (validationErrors.length === 0 ? 'success.light' : 'error.light') : 'grey.50',
                  mb: 2,
                }}
              >
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={loading || validating}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={loading || validating}
                  >
                    Seleccionar Archivo
                  </Button>
                </label>

                {validating && (
                  <Box sx={{ mt: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Validando archivo...
                    </Typography>
                  </Box>
                )}

                {file && !validating && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color={validationErrors.length === 0 ? 'success.main' : 'error.main'}>
                      {validationErrors.length === 0 ? '✓' : '✗'} {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024).toFixed(2)} KB - {parsedData.length} registros
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* VALIDACIÓN: Errores encontrados */}
              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Se encontraron {validationErrors.length} errores
                  </Typography>
                  <List dense>
                    {validationErrors.slice(0, 5).map((error, index) => (
                      <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemText 
                          primary={error}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                    {validationErrors.length > 5 && (
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText 
                          primary={`... y ${validationErrors.length - 5} errores más`}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Alert>
              )}

              {/* VALIDACIÓN: Todo correcto */}
              {file && validationErrors.length === 0 && !validating && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    ✓ Validación exitosa - {parsedData.length} registros listos para cargar
                  </Typography>
                </Alert>
              )}

              {/* Botones */}
              <Grid container spacing={2}>
                {/* Ver preview */}
                {parsedData.length > 0 && validationErrors.length === 0 && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={() => setShowPreview(true)}
                      fullWidth
                    >
                      Ver Preview de Datos
                    </Button>
                  </Grid>
                )}

                {/* Procesar */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleUpload}
                    disabled={!file || loading || validationErrors.length > 0}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Procesar y Cargar'}
                  </Button>
                </Grid>
              </Grid>

              {/* Errores */}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Resultados */}
              {results && (
                <Box sx={{ mt: 3 }}>
                  <Alert 
                    severity={results.errors.length === 0 ? 'success' : 'warning'}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Procesamiento completado
                    </Typography>
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="text.secondary">
                          {results.total}
                        </Typography>
                        <Typography variant="caption">
                          Total
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main">
                          {results.success}
                        </Typography>
                        <Typography variant="caption">
                          Exitosos
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="error.main">
                          {results.errors.length}
                        </Typography>
                        <Typography variant="caption">
                          Errores
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Lista de errores del servidor */}
                  {results.errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        <ErrorIcon fontSize="small" color="error" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Errores del servidor:
                      </Typography>
                      <List dense>
                        {results.errors.slice(0, 10).map((err, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`Fila ${err.row}`}
                              secondary={err.error}
                            />
                          </ListItem>
                        ))}
                        {results.errors.length > 10 && (
                          <ListItem>
                            <ListItemText
                              secondary={`... y ${results.errors.length - 10} errores más`}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}

                  {/* Advertencias */}
                  {results.warnings && results.warnings.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        <WarningIcon fontSize="small" color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Advertencias:
                      </Typography>
                      <List dense>
                        {results.warnings.map((warn, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`Fila ${warn.row}`}
                              secondary={warn.warning}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* BULK: Notas importantes */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Validación automática:</strong> El sistema valida los datos antes de enviarlos al servidor. 
          Revisa los errores y corrígelos en el Excel antes de procesar.
        </Typography>
      </Alert>

      {/* PREVIEW: Modal de preview */}
      <Dialog open={showPreview} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogTitle>
          Preview de Datos - {parsedData.length} registros
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {parsedData.length > 0 && Object.keys(parsedData[0]).map((key) => (
                    <TableCell key={key}>{key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.slice(0, 10).map((row, index) => (
                  <TableRow key={index}>
                    {Object.values(row).map((value, i) => (
                      <TableCell key={i}>{String(value)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {parsedData.length > 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Mostrando 10 de {parsedData.length} registros
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BulkUpload;
